import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
    ArrowLeft, MapPin, Hospital, Pill, Activity, Dumbbell, 
    Building2, ExternalLink, Navigation, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const facilityTypes = [
    { id: 'hospital', name: 'Hospitals', icon: Hospital, color: 'text-red-500' },
    { id: 'emergency', name: 'Emergency', icon: Activity, color: 'text-red-600' },
    { id: 'clinic', name: 'Clinics', icon: Building2, color: 'text-blue-500' },
    { id: 'physiotherapy', name: 'Physiotherapy', icon: Activity, color: 'text-green-500' },
    { id: 'pharmacy', name: 'Pharmacy', icon: Pill, color: 'text-purple-500' },
    { id: 'gym', name: 'Gym & Fitness', icon: Dumbbell, color: 'text-orange-500' }
];

const mapContainerStyle = {
    width: '100%',
    height: '400px',
    borderRadius: '12px'
};

export default function NearbyFacilities() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [location, setLocation] = useState(null);
    const [selectedType, setSelectedType] = useState('hospital');
    const [facilityUrls, setFacilityUrls] = useState(null);

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                    setLoading(false);
                },
                () => {
                    // Default to London if geolocation fails
                    setLocation({ lat: 51.5074, lng: -0.1278 });
                    setLoading(false);
                }
            );
        } else {
            setLocation({ lat: 51.5074, lng: -0.1278 });
            setLoading(false);
        }
    }, []);

    const fetchFacilityUrls = async () => {
        if (!location) return;
        try {
            const response = await axios.get(`${API}/facilities/nearby`, {
                params: { lat: location.lat, lng: location.lng, facility_type: selectedType }
            });
            setFacilityUrls(response.data);
        } catch (err) {
            console.error('Failed to fetch facilities');
        }
    };

    useEffect(() => {
        if (location) {
            fetchFacilityUrls();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, selectedType]);

    const openInGoogleMaps = () => {
        if (facilityUrls?.google_maps_url) {
            window.open(facilityUrls.google_maps_url, '_blank');
        }
    };

    const getDirections = () => {
        if (facilityUrls?.directions_url) {
            window.open(facilityUrls.directions_url, '_blank');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
                    <p>Getting your location...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background py-8 lg:py-12" data-testid="nearby-facilities-page">
            <div className="max-w-4xl mx-auto px-4">
                <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
                </Button>
                
                <h1 className="font-heading text-3xl font-bold mb-2">Nearby Healthcare Facilities</h1>
                <p className="text-muted-foreground mb-8">Find hospitals, clinics, pharmacies and more near you</p>

                {/* Facility Type Selector */}
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-6">
                    {facilityTypes.map(({ id, name, icon: Icon, color }) => (
                        <button
                            key={id}
                            onClick={() => setSelectedType(id)}
                            className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 text-center
                                ${selectedType === id 
                                    ? 'border-primary bg-primary/10' 
                                    : 'border-slate-200 dark:border-slate-700 hover:border-primary/50'}`}
                            data-testid={`facility-${id}`}
                        >
                            <Icon className={`w-5 h-5 ${color}`} />
                            <span className="text-xs font-medium">{name}</span>
                        </button>
                    ))}
                </div>

                {/* Map */}
                <Card className="mb-6">
                    <CardContent className="p-0 overflow-hidden rounded-xl">
                        {GOOGLE_MAPS_API_KEY ? (
                            <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    center={location}
                                    zoom={14}
                                >
                                    <Marker position={location} />
                                </GoogleMap>
                            </LoadScript>
                        ) : (
                            <div className="h-[400px] bg-muted flex items-center justify-center">
                                <div className="text-center">
                                    <MapPin className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                                    <p className="text-muted-foreground">Map loading...</p>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Action Buttons */}
                <div className="grid md:grid-cols-2 gap-4">
                    <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={openInGoogleMaps}>
                        <CardContent className="p-6 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                                <MapPin className="w-6 h-6 text-primary" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold">View on Google Maps</h3>
                                <p className="text-sm text-muted-foreground">
                                    Search for {facilityTypes.find(f => f.id === selectedType)?.name} nearby
                                </p>
                            </div>
                            <ExternalLink className="w-5 h-5 text-muted-foreground" />
                        </CardContent>
                    </Card>

                    <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={getDirections}>
                        <CardContent className="p-6 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center">
                                <Navigation className="w-6 h-6 text-success" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold">Get Directions</h3>
                                <p className="text-sm text-muted-foreground">
                                    Navigate to nearest {facilityTypes.find(f => f.id === selectedType)?.name.toLowerCase()}
                                </p>
                            </div>
                            <ExternalLink className="w-5 h-5 text-muted-foreground" />
                        </CardContent>
                    </Card>
                </div>

                {/* Quick Links */}
                <Card className="mt-6">
                    <CardHeader>
                        <CardTitle className="text-lg">Quick Emergency Links</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-2 gap-3">
                        <Button variant="destructive" className="rounded-full" onClick={() => window.location.href = 'tel:911'}>
                            Call Emergency (911)
                        </Button>
                        <Button variant="outline" className="rounded-full" onClick={() => window.open(`https://www.google.com/maps/search/emergency+room/@${location?.lat},${location?.lng},14z`, '_blank')}>
                            Find Emergency Room
                        </Button>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
